// Notes de frais: pure helpers. The server decides everything again (backend/finance/claims.py); these only name states
// and check the form before the round-trip. Amounts stay text in the currency's own precision (lib/finance.js), never floats.
import { EXPONENTS, minorToText, toMinor } from "./finance";

export const CLAIM_STATUS_LABELS = { draft: "Brouillon", pending_review: "En attente d'approbation", submitted: "Approuvée", rejected: "Refusée" };
const REIMBURSEMENT_LABELS = {
  to_reimburse: "À rembourser", partially_reimbursed: "Remboursée en partie", reimbursed: "Remboursée",
  a_decider: "À décider", apport: "Convertie en apport", abandon: "Non remboursée",
};

// What the person sees first: a draft sent back is « À corriger », an approved one shows where its reimbursement stands.
export function claimState(claim) {
  if (claim.status === "draft" && claim.review?.status === "to_correct") return { label: "À corriger", tone: "warning" };
  if (claim.status === "submitted") {
    const r = claim.reimbursement_status;
    return { label: REIMBURSEMENT_LABELS[r] || "Approuvée", tone: r === "reimbursed" ? "success" : r === "abandon" ? "muted" : "info" };
  }
  if (claim.status === "rejected") return { label: "Refusée", tone: "danger" };
  if (claim.status === "pending_review") return { label: "En attente d'approbation", tone: "info" };
  return { label: "Brouillon", tone: "muted" };
}

export const emptyClaim = (currency = "XOF", today = new Date().toISOString().slice(0, 10)) => ({
  title: "", description: "", incurred_at: today, amount: "", currency, category_id: "", supplier: "", mission_id: "", claimant_id: "",
});

export function claimProblem(form, { today = new Date().toISOString().slice(0, 10), receipts = 0, submitting = false } = {}) {
  if (!form.title.trim()) return "Indiquez ce que vous avez payé (ex. : Taxi aéroport).";
  if (!form.amount || toMinor(form.amount, form.currency) === null) return "Montant invalide.";
  if (!form.category_id) return "Choisissez une catégorie.";
  if (!form.incurred_at) return "Indiquez la date.";
  if (form.incurred_at > today) return "La date ne peut pas être dans le futur.";
  if (submitting && receipts === 0) return "Ajoutez la photo du reçu avant d'envoyer.";
  return null;
}

export function claimPayload(form, attachmentIds) {
  const out = {
    title: form.title.trim(), description: (form.description || "").trim(), incurred_at: form.incurred_at, amount: minorToText(toMinor(form.amount, form.currency), form.currency),
    currency: form.currency, category_id: form.category_id, attachment_ids: attachmentIds,
  };
  if ((form.supplier || "").trim()) out.supplier = form.supplier.trim();
  if (form.mission_id) out.mission_id = form.mission_id;
  if (form.claimant_id) out.claimant_id = form.claimant_id;
  return out;
}

export const CURRENCIES = Object.keys(EXPONENTS);

// --- 14-3: caps (signalled, never blocking) and mission advances ---------------------------------------------------------------
// The cap of a category, compared only in its own currency (never converted). Returns the cap text when the amount exceeds it.
export function capExceeded(category, amount, currency) {
  if (!category?.cap || category.cap_currency !== currency) return null;
  const minor = toMinor(amount, currency);
  const cap = toMinor(category.cap, currency);
  return minor !== null && cap !== null && minor > cap ? category.cap : null;
}

export const ADVANCE_KINDS = { given: "Avance remise", returned: "Restitution" };
export const ADVANCE_METHODS = {
  especes: "Espèces", wave: "Wave", orange_money: "Orange Money", virement: "Virement", cheque: "Chèque", autre: "Autre",
};

export const emptyAdvance = (currency = "XOF", today = new Date().toISOString().slice(0, 10)) => ({
  kind: "given", collaborator_id: "", amount: "", currency, on: today, method: "especes", note: "",
});

export function advanceProblem(form, { today = new Date().toISOString().slice(0, 10), held = null } = {}) {
  if (!form.collaborator_id) return "Choisissez la personne.";
  const minor = toMinor(form.amount, form.currency);
  if (!form.amount || minor === null || minor <= 0) return "Montant invalide.";
  if (!form.on) return "Indiquez la date.";
  if (form.on > today) return "La date ne peut pas être dans le futur.";
  if (form.kind === "returned" && held !== null && minor > (toMinor(held, form.currency) || 0)) return "C'est plus que l'avance que la personne détient encore.";
  return null;
}

export function advancePayload(form, missionId) {
  const out = {
    kind: form.kind, collaborator_id: form.collaborator_id, amount: minorToText(toMinor(form.amount, form.currency), form.currency),
    currency: form.currency, on: form.on, method: form.method,
  };
  if ((form.note || "").trim()) out.note = form.note.trim();
  if (missionId) out.mission_id = missionId;
  return out;
}
